import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Center,
  Container,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import {
  getPublicUnsubscribeInfo,
  submitPublicUnsubscribe,
} from "../../api/partnersApi";
import AmbientBlobs from "../../marketing/components/AmbientBlobs";
import "../../marketing/styles.css";
import "./landing-form.css";

const Unsubscribe = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation("public");
  const toast = useToast();

  useEffect(() => {
    document.body.classList.add("marketing-page");
    document.body.classList.add("is-landing-unsubscribe");
    return () => {
      document.body.classList.remove("marketing-page");
      document.body.classList.remove("is-landing-unsubscribe");
    };
  }, []);

  useEffect(() => {
    const explicit = window.localStorage.getItem("portalLanguage");
    if (!explicit) {
      i18n.changeLanguage("fr");
    }
  }, [i18n]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getPublicUnsubscribeInfo(token);
        if (cancelled) return;
        setInfo(data);
        if (data.alreadyUnsubscribed) setDone(true);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError(
            t("unsubscribe.invalidToken", "Ce lien de désinscription est invalide ou a expiré.")
          );
        } else {
          setError(t("unsubscribe.loadFailed", "Une erreur est survenue. Veuillez réessayer."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await submitPublicUnsubscribe(token);
      setDone(true);
      if (res?.alreadyUnsubscribed) {
        setInfo((prev) => ({ ...(prev || {}), alreadyUnsubscribed: true }));
      }
    } catch {
      toast({
        title: t("unsubscribe.error", "La désinscription a échoué."),
        status: "error",
        duration: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <Box
      as="header"
      className="lf-header"
      display="flex"
      alignItems="center"
      justifyContent="center"
      pt={{ base: 6, md: 10 }}
      pb={{ base: 4, md: 6 }}
    >
      <Box className="lf-brand" display="flex" alignItems="center" gap="10px" data-testid="asap-hub-logo">
        <span className="mk-brand-mark" aria-hidden="true" />
        <Text
          as="span"
          fontWeight="900"
          letterSpacing="-0.045em"
          color="white"
          fontSize="22px"
          sx={{ marginBottom: "0 !important" }}
        >
          ASAP Hub
        </Text>
      </Box>
    </Box>
  );

  return (
    <Box className="lf-shell" position="relative">
      <AmbientBlobs />
      {header}

      <Container maxW="container.sm" pb={16}>
        <Box className="lf-card lf-card-state">
          {loading ? (
            <Center py={6}>
              <Spinner size="lg" color="#79f5c7" thickness="4px" />
            </Center>
          ) : error ? (
            <VStack spacing={3}>
              <Text
                as="h1"
                className="lf-title"
                fontSize="24px"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("unsubscribe.cannotProcess", "Désinscription impossible")}
              </Text>
              <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
                {error}
              </Text>
            </VStack>
          ) : done ? (
            <VStack spacing={3} data-testid="unsubscribe-success">
              <Text
                as="h1"
                className="lf-title"
                fontSize="28px"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("unsubscribe.successTitle", "Vous êtes désinscrit·e")}
              </Text>
              <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
                {t(
                  "unsubscribe.successBody",
                  "Vous ne recevrez plus d'emails de cette liste."
                )}
              </Text>
            </VStack>
          ) : (
            <VStack spacing={5} data-testid="unsubscribe-confirm">
              <Text
                as="h1"
                className="lf-title"
                fontSize="26px"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("unsubscribe.title", "Confirmer la désinscription")}
              </Text>
              <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
                {t(
                  "unsubscribe.confirmPrompt",
                  "Vous êtes sur le point de vous désinscrire ({{email}}). Souhaitez-vous confirmer ?",
                  { email: info?.emailMasked || "" }
                )}
              </Text>
              {info?.poolName && (
                <Text
                  fontSize="12px"
                  color="#8fa0b7"
                  sx={{ marginBottom: "0 !important" }}
                >
                  {t("unsubscribe.poolName", "Liste : {{name}}", { name: info.poolName })}
                </Text>
              )}
              <button
                onClick={handleConfirm}
                className="mk-btn mk-btn-primary lf-submit"
                disabled={submitting}
                data-testid="unsubscribe-confirm-button"
              >
                {submitting
                  ? t("landing.submitting", "Envoi…")
                  : t("unsubscribe.confirmCta", "Me désinscrire")}
              </button>
            </VStack>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Unsubscribe;
